import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import { Sparkles, TrendingUp, TrendingDown, Minus, Brain } from "lucide-react";
import { api } from "@/lib/api";
import { EDI } from "@/constants/testIds";

const TREND_ICON = { up: TrendingUp, down: TrendingDown, flat: Minus };
const TREND_COLOR = { up: "text-emerald-400", down: "text-red-400", flat: "text-slate-400" };

export default function KnowledgeIntelligence() {
    const [data, setData] = useState(null);

    useEffect(() => {
        api.get("/edi/knowledge-intelligence")
            .then((r) => setData(r.data))
            .catch(() => toast.error("Failed to load Knowledge Intelligence"));
    }, []);

    if (!data) return <div className="kicker text-slate-500">Loading Knowledge Intelligence…</div>;

    return (
        <div className="space-y-8" data-testid={EDI.knowledgeIntelligenceRoot}>
            <div className="flex items-end justify-between gap-6 flex-wrap">
                <div>
                    <div className="kicker text-[var(--sgsb-gold)]">Enterprise Delivery Intelligence™</div>
                    <h1 className="mt-2 font-display font-black text-white text-4xl md:text-5xl tracking-tighter">
                        Knowledge Intelligence™
                    </h1>
                    <p className="mt-2 text-slate-400 max-w-2xl font-body text-sm">
                        The health, coverage, quality and confidence of SGS Bateman's
                        engineering knowledge as a strategic corporate asset.
                    </p>
                </div>
                <div className="flex items-center gap-2 text-[var(--sgsb-gold)] text-xs font-body">
                    <Brain className="h-4 w-4" strokeWidth={1.5} />
                    <span className="kicker text-[10px]">12 KPIs · live</span>
                </div>
            </div>

            {/* KPI grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
                {data.kpis.map((k) => {
                    const Icon = TREND_ICON[k.trend] || Minus;
                    const color = TREND_COLOR[k.trend] || "text-slate-400";
                    return (
                        <div key={k.key} className="glass hover-lift rounded-xl p-5 relative overflow-hidden" data-testid={EDI.knowledgeKpi(k.key)}>
                            <div className="absolute top-0 left-0 h-[2px] w-10 bg-[var(--sgsb-gold)]" />
                            <div className="flex items-start justify-between">
                                <Sparkles className="h-4 w-4 text-[var(--sgsb-gold)]" strokeWidth={1.5} />
                                <Icon className={`h-3.5 w-3.5 ${color}`} strokeWidth={2} />
                            </div>
                            <div className="mt-2 kicker text-slate-400 text-[10px]">{k.label}</div>
                            <div className="mt-1 font-display font-black text-white text-xl tracking-tight">{k.value}</div>
                            <div className="mt-1 text-[11px] text-slate-500 font-body">{k.context}</div>
                        </div>
                    );
                })}
            </div>

            {/* Growth curve + discipline table */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
                <div className="glass rounded-xl p-6 xl:col-span-2">
                    <div className="kicker text-slate-400">Knowledge growth</div>
                    <h3 className="font-display font-black text-white text-xl mt-1 tracking-tight">
                        Corporate knowledge base — assets & portfolio reuse rate
                    </h3>
                    <div className="h-[300px] mt-5 -ml-3">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={data.growth_curve}>
                                <CartesianGrid strokeDasharray="3 6" stroke="rgba(148,163,184,0.12)" vertical={false} />
                                <XAxis dataKey="quarter" stroke="#64748B" tick={{ fontSize: 11, fontFamily: "Manrope" }} />
                                <YAxis yAxisId="l" stroke="#64748B" tick={{ fontSize: 11, fontFamily: "Manrope" }} />
                                <YAxis yAxisId="r" orientation="right" stroke="#64748B" tick={{ fontSize: 11, fontFamily: "Manrope" }} />
                                <Tooltip contentStyle={{ background: "#0F172A", border: "1px solid rgba(212,175,55,0.35)", borderRadius: 8, color: "#F8FAFC" }} />
                                <Legend wrapperStyle={{ color: "#94A3B8", fontSize: 11 }} />
                                <Line yAxisId="l" dataKey="assets_m" name="Assets (M)" stroke="#D4AF37" strokeWidth={2.4} dot={false} />
                                <Line yAxisId="r" dataKey="reuse_pct" name="Reuse %" stroke="#3B82F6" strokeWidth={2} dot={false} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="glass rounded-xl overflow-hidden">
                    <div className="p-4 border-b border-slate-800/60">
                        <h3 className="font-display font-black text-white tracking-tight">By discipline</h3>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm font-body">
                            <thead>
                                <tr className="text-slate-500 kicker text-[10px]">
                                    <th className="text-left px-4 py-3">Discipline</th>
                                    <th className="text-right px-4 py-3">Assets</th>
                                    <th className="text-right px-4 py-3">Health</th>
                                    <th className="text-right px-4 py-3">Reuse</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.by_discipline.map((d) => (
                                    <tr key={d.discipline} className="border-t border-slate-800/60">
                                        <td className="px-4 py-2 text-slate-100">{d.discipline}</td>
                                        <td className="px-4 py-2 text-right text-slate-300">{d.assets.toLocaleString()}</td>
                                        <td className="px-4 py-2 text-right text-emerald-400 font-display font-bold">{d.health}%</td>
                                        <td className="px-4 py-2 text-right text-[var(--sgsb-gold)] font-display font-bold">{d.reuse}%</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}
